<script lang="ts">
import { useInstantWinsStore } from '@/stores/instant-wins'
import { usePrizesStore } from '@/stores/prizes'
import type { Image, InstantWinDraw, InstantWinMainPrize } from '@/types'
import { Icons, InstantWinType, type TIconValue } from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'
import { RouterLink } from 'vue-router'

import GameCard from './GameCard.vue'

export default defineComponent({
  components: { GameCard, RouterLink },
  props: {
    game: {
      type: Object as PropType<InstantWinDraw>,
      required: true,
    },
    progressLoading: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    return {
      prizesStore: usePrizesStore(),
      Icons,
    }
  },
  computed: {
    prizesLeft(): number {
      return (
        this.game.total_remaining_prizes ??
        this.game.prizes.reduce(
          (prev, curr) =>
            prev + curr.prizes_available_count - curr.prizes_won_count,
          0
        )
      )
    },
    /**
     * Determine if this is a mystery box game to apply countdown text to
     */
    countdownText(): string | undefined {
      // Check if the game contains 'countdown_text' to identify mystery boxes
      return this.game.countdown_text || this.game.description
    },
    prize(): InstantWinMainPrize {
      return this.game.main_prize
    },
    displayImage(): Image {
      if (!this.$settings.isGbLocale() && this.prize.euro_card_image) {
        return this.prize.euro_card_image
      }

      // Default to regular card_image
      return this.prize.card_image
    },
    icon(): TIconValue {
      switch (this.game.instant_win_type_id) {
        case InstantWinType.Standard:
          return Icons.Lightening
        case InstantWinType.MysteryBox:
          return Icons.Present
        case InstantWinType.InstantPack:
          return Icons.CardPack
        case InstantWinType.InstantSpin:
          return Icons.SlotMachine
        default:
          return Icons.Lightening
      }
    },
  },
  async mounted() {
    if (this.game.total_remaining_prizes == null) {
      const instantWinsStore = useInstantWinsStore()
      await instantWinsStore.fetchInitialCounters(this.game)
    }
  },
})
</script>

<template>
  <RouterLink
    v-if="game"
    :to="{
      name: 'instant-win',
      params: { instantWinCode: prize.code },
    }"
    data-cy="instant-win-card"
  >
    <GameCard
      :title="game.title"
      :ticket-cost="game.ticket_cost"
      :image="displayImage.src"
      :prizes-left="prizesLeft"
      :total-prize-value="game.total_prize_value"
      :close-date="prize.close_at"
      :countdown-text="countdownText"
      :icon="icon"
    />
  </RouterLink>
</template>
